import type { CompetitionTab } from '@alison/analytics';
import { AnalyticsService } from '@alison/analytics/client';
import Box from '@mui/material/Box';
import CircularProgress from '@mui/material/CircularProgress';
import Tab from '@mui/material/Tab';
import Tabs from '@mui/material/Tabs';
import Typography from '@mui/material/Typography';
import { createFileRoute, useNavigate } from '@tanstack/react-router';
import { type ReactNode, useCallback, useMemo } from 'react';
import { AppShell } from '../../components/AppShell.js';
import { CompetitionTopMetricsPanel } from '../../components/competition/CompetitionTopMetricsPanel.js';
import { CompetitionCreativesBoard } from '../../components/competition/creatives/CompetitionCreativesBoard.js';
import {
  type CompetitionCreativesScope,
  type CompetitionTopMetricsScope,
  useCompetitionTopMetrics,
} from '../../components/competition/creatives/data/api.js';
import { CompetitionElementsBoard } from '../../components/competition/elements/CompetitionElementsBoard.js';
import {
  appliedSelection,
  CompetitionFilterBar,
} from '../../components/filters/CompetitionFilterBar.js';
import { useScopeSelection } from '../../components/filters/useScopeSelection.js';
import {
  type CompetitionSearch,
  searchFromFilters,
  useCompetitionFilterSearch,
  validateCompetitionFilterSearch,
} from '../../lib/competition-filter-search.js';
import { dateWindowFromRange } from '../../lib/dateUtils.js';
import { useAuthenticatedSession } from '../../lib/useAuthenticatedSession.js';
import { useShellAccount } from '../../lib/useShellAccount.js';

const ELEMENTS_TAB: CompetitionTab = 'elements';

type CompetitionPageSearch = Record<string, string[] | CompetitionTab>;

/** The tab rides in the URL so a drill-down from Elements lands on Creatives in one navigation. */
function validateCompetitionSearch(search: Record<string, unknown>): CompetitionPageSearch {
  return {
    ...validateCompetitionFilterSearch(search),
    ...(search['tab'] === ELEMENTS_TAB ? { tab: ELEMENTS_TAB } : {}),
  };
}

function CompetitionPage(): ReactNode {
  const { session, isPending, onSignOut } = useAuthenticatedSession();
  const account = useShellAccount(session?.user.email);
  const { scope, applyScope, accountIds } = useScopeSelection();
  const { added } = useCompetitionFilterSearch();
  const navigate = useNavigate();
  const { tab: tabParam } = Route.useSearch();
  const tab: CompetitionTab = tabParam === ELEMENTS_TAB ? ELEMENTS_TAB : 'creatives';
  const narrowed = useMemo(() => appliedSelection(added), [added]);
  const selected = narrowed as CompetitionCreativesScope['selected'];
  const dateWindow = useMemo(() => dateWindowFromRange(scope.range, new Date()), [scope.range]);

  const topMetricsScope = useMemo<CompetitionTopMetricsScope>(
    () => ({
      dateRange: dateWindow,
      ...(accountIds.length ? { accountIds } : {}),
      ...(Object.keys(narrowed).length ? { filters: narrowed } : {}),
    }),
    [accountIds, dateWindow, narrowed],
  );
  const topMetrics = useCompetitionTopMetrics(topMetricsScope, accountIds.length > 0);

  function setTab(next: CompetitionTab): void {
    void navigate({
      to: '.',
      replace: true,
      search: (previous) => ({ ...previous, tab: next === ELEMENTS_TAB ? next : undefined }),
    });
  }

  const openCreatives = useCallback(
    (feature: string, tag: string | null): void => {
      const narrowedFilters = added.filter(
        (filter) => filter.key !== 'feature' && filter.key !== 'tag',
      );
      const next = [
        ...narrowedFilters,
        { key: 'feature' as const, values: [feature] },
        ...(tag === null ? [] : [{ key: 'tag' as const, values: [tag] }]),
      ];
      void navigate({
        to: '.',
        search: (previous) => ({
          ...searchFromFilters(previous as CompetitionSearch, next),
          tab: undefined,
        }),
      });
    },
    [added, navigate],
  );

  if (isPending || session === null) {
    return (
      <Box
        sx={{ display: 'flex', minHeight: '100vh', alignItems: 'center', justifyContent: 'center' }}
      >
        <CircularProgress />
      </Box>
    );
  }

  return (
    <AppShell
      account={account}
      onLogout={onSignOut}
      active="explore"
      topBar={<CompetitionFilterBar scope={scope} onScopeChange={applyScope} />}
    >
      <CompetitionTopMetricsPanel
        data={topMetrics.data}
        isLoading={topMetrics.isLoading}
        isError={topMetrics.isError}
      />
      <Box sx={{ px: 6, pt: 3 }}>
        <Typography variant="h2" component="h1" sx={{ mb: 4 }}>
          Competitors
        </Typography>
        <Tabs
          value={tab}
          onChange={(_event, next: CompetitionTab) => {
            AnalyticsService.track('Competition - Tab Changed', { tab: next });
            setTab(next);
          }}
        >
          <Tab value="creatives" label="Creatives" />
          <Tab value="elements" label="Elements" />
        </Tabs>
      </Box>
      {tab === 'creatives' ? (
        <CompetitionCreativesBoard
          accountIds={accountIds}
          dateWindow={dateWindow}
          selected={selected}
        />
      ) : (
        <CompetitionElementsBoard
          accountIds={accountIds}
          dateWindow={dateWindow}
          filters={narrowed}
          onOpenCreatives={openCreatives}
        />
      )}
    </AppShell>
  );
}

export const Route = createFileRoute('/competition/')({
  component: CompetitionPage,
  validateSearch: validateCompetitionSearch,
});
